/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useState } from "react";
import { Alert } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";

import api from "../../api/index.js";
import { Loader } from "../loader";
import { LoadingSpinner } from "../loading_spinner";
import { StyledMenuItem } from "../styled_menu_item/index.jsx";
import { useOtMutation } from "../../hooks/mutations/useOtMutation.js";
import { networkGuestMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/networkGuestMutationKeys.js";
import { validatePhoneNumber } from "../../utils/validatePhoneNumber.js";
import { validateZipCode, ZIP_CODE_ERROR_MESSAGE } from "../../utils/validateZipCode.js";
import { useNotificationContext } from "../../contexts/notificationContext";
import {
  StyledAddButton,
  StyledCrossButton,
  StyledDatePicker,
  StyledFormFieldText,
  StyledHelperText,
  StyledPhoneField,
  StyledSearchIconButton,
  StyledSelect,
  StyledTextField,
  StyledTypography,
} from "../../pages/network_guests/styles.jsx";
import {
  StyledCloseButton,
  StyledDialog,
  StyledFieldContainer,
  StyledModalActions,
  StyledModalContent,
  StyledModalHeader,
  StyledModalTitle,
  StyledPhoneRow,
} from "./styles.jsx";
import { ModalCancelButton } from "../modal_cancel_button/index.jsx";

// Humanize a snake_case error key into a field label, e.g. "first_name" -> "First name".
const humanizeField = (field) =>
  field.replace(/_/g, " ").replace(/^\w/, (char) => char.toUpperCase());

const getFirstErrorMessage = (errors) => {
  if (!errors || typeof errors !== "object") {
    return null;
  }

  if (errors.already_exists) {
    return Array.isArray(errors.already_exists)
      ? errors.already_exists[0]
      : errors.already_exists;
  }

  const [field, messages] = Object.entries(errors)[0] || [];
  if (!field) {
    return null;
  }

  const message = Array.isArray(messages) ? messages[0] : messages;
  return message ? `${humanizeField(field)} ${message}` : null;
};

const emptyNetworkGuest = (restaurantId) => ({
  first_name: null,
  last_name: null,
  phone_number: "",
  email: null,
  zip_code: "",
  date_of_birth: "",
  restaurant_ids: restaurantId ? [restaurantId] : [],
});

export const NetworkGuestCreateModal = ({
  open,
  onClose,
  restaurantId,
  restaurantName,
  restaurantGroupName,
}) => {
  const showNotification = useNotificationContext();

  const [newGuest, setNewGuest] = useState(emptyNetworkGuest(restaurantId));
  const [errors, setErrors] = useState({});
  const [guestFetched, setGuestFetched] = useState(false);
  const [guestExist, setGuestExist] = useState(false);
  const [guestFetchLoading, setGuestFetchLoading] = useState(false);
  const [associatedRestaurants, setAssociatedRestaurants] = useState([]);

  const resetState = () => {
    setNewGuest(emptyNetworkGuest(restaurantId));
    setErrors({});
    setGuestFetched(false);
    setGuestExist(false);
    setGuestFetchLoading(false);
    setAssociatedRestaurants([]);
  };

  // Re-seed the form whenever the modal is (re)opened or the restaurant changes.
  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, restaurantId]);

  const { mutate: addGuestMutation, isPending: loading } = useOtMutation({
    mutationFn: (data) => api.post("/v1/admin/network_guests", { network_guest: data }),
    invalidateKeys: networkGuestMutationKeyGroups.create,
    onSuccess: () => {
      showNotification(
        guestExist
          ? "Network guest has been updated successfully"
          : "Network guest has been added successfully",
        "success"
      );
      resetState();
      onClose();
    },
    // Surface a clear, field-aware message (e.g. "Email can't be blank") instead of the
    // bare validation string. Falls back to the generic mutation error when unavailable.
    errorMessage: ({ error }) => getFirstErrorMessage(error?.response?.data?.errors),
    onError: (error) => {
      setErrors(error?.response?.data?.errors);
    },
  });

  const handleInputChange = (value, field) => {
    setNewGuest({ ...newGuest, [field]: value });
  };

  const handleDateOfBirth = (event) => {
    setNewGuest({ ...newGuest, date_of_birth: dayjs(event) });
    if (errors["date_of_birth"] !== "") {
      setErrors({ ...errors, ["date_of_birth"]: "" });
    }
  };

  const searchNetworkGuest = async () => {
    if (!validatePhoneNumber(newGuest?.phone_number)) {
      setErrors((prev) => ({ ...prev, phone_number: "Phone number is required" }));
      return;
    }

    // Clear any stale form errors from a previous attempt before re-searching.
    setErrors({});
    setGuestFetchLoading(true);
    try {
      const res = await api.get("/v1/admin/network_guests/find_guest_by_number", {
        params: { phone_number: newGuest?.phone_number },
      });
      const existingRestaurants = res?.data?.[0]?.investor_restaurants?.map(
        (restaurant) => restaurant?.id
      );
      setNewGuest({
        ...newGuest,
        first_name: res?.data?.[0]?.first_name,
        last_name: res?.data?.[0]?.last_name,
        email: res?.data?.[0]?.email,
        zip_code: res?.data?.[0]?.zip_code,
        date_of_birth: res?.data?.[0]?.date_of_birth,
        // Always associate the current restaurant on top of the guest's existing ones.
        restaurant_ids: Array.from(
          new Set([...(existingRestaurants || []), restaurantId].filter(Boolean))
        ),
      });
      setAssociatedRestaurants(res?.data?.[0]?.investor_restaurants || []);
      setGuestExist(true);
      setGuestFetched(true);
    } catch (error) {
      // Only a genuine 404 means "no guest for this phone" and is safe to proceed
      // with creation. Transport/server errors (timeout, 5xx, 403, …) must NOT fall
      // through to the create form, otherwise the user could create a duplicate
      // guest for a phone number that actually already exists.
      if (error?.response?.status === 404) {
        setGuestExist(false);
        setGuestFetched(true);
      } else {
        setGuestExist(false);
        setGuestFetched(false);
        showNotification(
          "Unable to verify this phone number right now. Please try again.",
          "error"
        );
      }
    } finally {
      setGuestFetchLoading(false);
    }
  };

  const handleClearNetworkGuest = () => {
    setGuestExist(false);
    setGuestFetched(false);
    setNewGuest(emptyNetworkGuest(restaurantId));
    setAssociatedRestaurants([]);
  };

  const handleAddNetworkGuest = () => {
    if (!restaurantId) {
      setErrors((prev) => ({ ...prev, restaurant_id: "Please select a restaurant" }));
      showNotification("Please select a restaurant", "error");
      return;
    }

    if (!validateZipCode(newGuest?.zip_code)) {
      setErrors((prev) => ({ ...prev, zip_code: ZIP_CODE_ERROR_MESSAGE }));
      showNotification(ZIP_CODE_ERROR_MESSAGE, "error");
      return;
    }

    // Normalize blank required text fields to "" so the param is always present.
    // An untouched field left as null hits the "is required" input filter and short-circuits
    // the remaining presence validations, so only that field's error comes back. Sending ""
    // instead lets every required field fail presence together ("can't be blank"), surfacing
    // all field errors at once and a consistent message.
    let data = {
      ...newGuest,
      restaurant_ids: newGuest?.restaurant_ids?.length
        ? newGuest.restaurant_ids
        : [restaurantId],
      first_name: newGuest?.first_name ?? "",
      last_name: newGuest?.last_name ?? "",
      email: newGuest?.email ?? "",
    };
    if (data?.date_of_birth) {
      const currentDate = new Date(data?.date_of_birth);
      const utcDate = new Date(
        currentDate.getTime() - currentDate.getTimezoneOffset() * 60 * 1000
      );
      data.date_of_birth = utcDate;
    }

    if (newGuest?.phone_number?.length <= 4) {
      delete data.phone_number;
    }

    addGuestMutation(data);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose} data-testid="network-guest-create-modal">
      <StyledModalHeader>
        <StyledModalTitle>Add Network Guest</StyledModalTitle>
        <StyledCloseButton onClick={handleClose} size="small">
          <CloseIcon />
        </StyledCloseButton>
      </StyledModalHeader>

      <StyledModalContent>
        <div>
          <StyledFormFieldText>
            Phone number<span style={{ color: "red" }}>*</span>
          </StyledFormFieldText>
          <StyledPhoneRow>
            <div style={{ width: "100%" }}>
              <StyledPhoneField
                value={newGuest?.phone_number}
                onChange={(e) => {
                  handleInputChange(e, "phone_number");
                  setErrors((prev) => ({ ...prev, phone_number: null }));
                }}
                defaultCountry="us"
                forceDialCode
                borderColor={errors?.phone_number ? "red" : "#c4c4c4"}
                data-testid="create-phone"
                disabled={guestFetched}
              />
            </div>
            {guestFetched ? (
              <StyledCrossButton>
                <ClearIcon onClick={handleClearNetworkGuest} />
              </StyledCrossButton>
            ) : (
              <StyledSearchIconButton
                aria-label="Search Network Guest by Phone"
                onClick={searchNetworkGuest}
                disabled={guestFetchLoading}
                disableRipple
              >
                <SearchIcon />
              </StyledSearchIconButton>
            )}
          </StyledPhoneRow>
          <StyledHelperText
            style={{ display: errors?.phone_number ? "inline" : "none" }}
          >
            {errors?.phone_number && `${errors?.phone_number}`}
          </StyledHelperText>
        </div>

        {guestFetchLoading && <Loader />}

        {guestFetched && (
          <>
            <StyledFieldContainer>
              {guestExist ? (
                <Alert variant="outlined" severity="error">
                  This phone belongs to a network guest record. Adding a network guest with
                  the same phone number would associate new record with the already existing
                  one.
                </Alert>
              ) : (
                <Alert variant="outlined" severity="info">
                  This phone does not belong to any network guest record. Please proceed by
                  filling the required details.
                </Alert>
              )}
            </StyledFieldContainer>

            {associatedRestaurants?.length > 0 && (
              <StyledFieldContainer>
                <StyledFormFieldText>Active Restaurants: </StyledFormFieldText>
                <StyledTypography variant="caption" color="text.secondary" display="block">
                  {associatedRestaurants.map((res) => res?.name).join(", ")}
                </StyledTypography>
              </StyledFieldContainer>
            )}

            <StyledFieldContainer>
              <StyledFormFieldText>Restaurant group</StyledFormFieldText>
              <StyledSelect
                value={restaurantGroupName || ""}
                disabled
                displayEmpty
                renderValue={() => restaurantGroupName || "Select"}
              >
                <StyledMenuItem value={restaurantGroupName || ""}>
                  {restaurantGroupName || "Select"}
                </StyledMenuItem>
              </StyledSelect>
            </StyledFieldContainer>

            <StyledFieldContainer>
              <StyledFormFieldText>
                Restaurant<span style={{ color: "red" }}>*</span>
              </StyledFormFieldText>
              <StyledSelect
                value={restaurantId || ""}
                disabled
                displayEmpty
                renderValue={() => restaurantName || "Select"}
              >
                <StyledMenuItem value={restaurantId || ""}>
                  {restaurantName || "Select"}
                </StyledMenuItem>
              </StyledSelect>
            </StyledFieldContainer>

            <StyledFieldContainer>
              <StyledFormFieldText>
                First name<span style={{ color: "red" }}>*</span>
              </StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => {
                  handleInputChange(e.target.value, "first_name");
                  setErrors((prev) => ({ ...prev, first_name: null }));
                }}
                error={errors?.first_name}
                helperText={errors?.first_name}
                value={newGuest?.first_name || ""}
                data-testid="create-first-name"
                inputProps={{ "data-testid": "create-first-name-input" }}
              />
            </StyledFieldContainer>

            <StyledFieldContainer>
              <StyledFormFieldText>
                Last name<span style={{ color: "red" }}>*</span>
              </StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => {
                  handleInputChange(e.target.value, "last_name");
                  setErrors((prev) => ({ ...prev, last_name: null }));
                }}
                error={errors?.last_name}
                helperText={errors?.last_name}
                value={newGuest?.last_name || ""}
                data-testid="create-last-name"
                inputProps={{ "data-testid": "create-last-name-input" }}
              />
            </StyledFieldContainer>

            <StyledFieldContainer>
              <StyledFormFieldText>
                Email address<span style={{ color: "red" }}>*</span>
              </StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => {
                  handleInputChange(e.target.value, "email");
                  setErrors((prev) => ({ ...prev, email: null }));
                }}
                error={errors?.email?.[0]}
                helperText={errors?.email?.[0]}
                value={newGuest?.email || ""}
                data-testid="create-email"
                inputProps={{ "data-testid": "create-email-input" }}
              />
            </StyledFieldContainer>

            <StyledFieldContainer>
              <StyledFormFieldText>Zip code</StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                inputProps={{ "data-testid": "create-zip-code-input" }}
                onChange={(e) => {
                  handleInputChange(e.target.value, "zip_code");
                  setErrors((prev) => ({ ...prev, zip_code: null }));
                }}
                error={Boolean(errors?.zip_code)}
                helperText={
                  Array.isArray(errors?.zip_code) ? errors?.zip_code?.[0] : errors?.zip_code
                }
                value={newGuest?.zip_code || ""}
                data-testid="create-zip-code"
              />
            </StyledFieldContainer>

            <StyledFieldContainer>
              <StyledFormFieldText>Date of Birth</StyledFormFieldText>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StyledDatePicker
                  value={dayjs(newGuest?.date_of_birth)}
                  onChange={(event) => handleDateOfBirth(event)}
                  slotProps={{
                    textField: {
                      placeholder: "Date of Birth",
                      inputProps: { "data-testid": "create-date-of-birth-input" },
                      error: !!errors?.date_of_birth,
                      helperText: errors?.date_of_birth?.[0]?.includes("must be less than")
                        ? "Cannot be a future or present date"
                        : errors?.date_of_birth,
                    },
                  }}
                />
              </LocalizationProvider>
            </StyledFieldContainer>
          </>
        )}
      </StyledModalContent>

      <StyledModalActions>
        <ModalCancelButton onClick={handleClose}>Cancel</ModalCancelButton>
        <StyledAddButton
          onClick={handleAddNetworkGuest}
          variant="contained"
          disabled={loading || !guestFetched}
        >
          {loading ? <LoadingSpinner isLoading={true} /> : guestExist ? "Update" : "Add"}
        </StyledAddButton>
      </StyledModalActions>
    </StyledDialog>
  );
};

export default NetworkGuestCreateModal;
